import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import SEO from "@/components/site/SEO";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { TOTAL, STEP_TITLES, initialState, validateStep, buildPayload, buildConsentPayload } from "@/pages/apply/applyForm";
import {
  Step1AdvanceDetails, Step2Applicant, Step3Account, Step4Brokerage,
  Step5Property, Step6Broker, Step7Closing, Step8Uploads,
} from "@/pages/apply/ApplySteps";
import ConsentControls from "@/components/consent/ConsentControls";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { LEGAL_DOCUMENTS } from "@/lib/legalDocuments";

const Step9 = ({ data, patch, showConsentError }) => (
  <ConsentControls data={data} patch={patch} showError={showConsentError} />
);

const STEP_COMPONENTS = {
  1: Step1AdvanceDetails,
  2: Step2Applicant,
  3: Step3Account,
  4: Step4Brokerage,
  5: Step5Property,
  6: Step6Broker,
  7: Step7Closing,
  8: Step8Uploads,
  9: Step9,
};

function SuccessScreen({ onDashboard, onHome, versions }) {
  return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center fade-up" data-testid="apply-success">
      <div className="lux-card p-14">
        <div className="w-20 h-20 rounded-full bg-[#F3D8DA] text-[#2A1C14] mx-auto flex items-center justify-center">
          <CheckCircle2 size={40} strokeWidth={1.5} />
        </div>
        <h1 className="font-serif-lux text-5xl mt-8">Your application has been submitted for review.</h1>
        <p className="mt-6 text-[#5A3B31] leading-relaxed max-w-xl mx-auto">
          Submission does not guarantee approval or funding. Hannah Howard Funding may contact you or authorized transaction participants to verify the information provided. Please retain this confirmation for your records.
        </p>

        {versions ? (
          <div className="mt-10 text-left max-w-lg mx-auto rounded-2xl bg-[#F4EEE8] border border-[#E8DFD8] p-5" data-testid="acknowledgment-manifest">
            <p className="overline mb-3">Documents you accepted</p>
            <ul className="space-y-1.5 text-xs text-[#5A3B31]">
              {Object.values(LEGAL_DOCUMENTS).slice(0, 5).map((d) => (
                <li key={d.id} className="flex items-center justify-between">
                  <span>{d.label}</span>
                  <span className="font-mono text-[10px] text-[#8C7A70]">{d.version}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-[#8C7A70] mt-4 leading-relaxed">
              Consent audit ID: <span className="font-mono">{versions.consent_record_id || "—"}</span>
            </p>
          </div>
        ) : null}

        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          <button onClick={onDashboard} className="btn-primary" data-testid="success-view-dashboard">View My Dashboard</button>
          <button onClick={onHome} className="btn-blush" data-testid="success-back-home">Back to Home</button>
        </div>
      </div>
    </div>
  );
}

export default function Apply() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, register, refresh } = useAuth();
  const [step, setStep] = useState(1);
  const [showConsentError, setShowConsentError] = useState(false);
  // Pre-fill net commission / advance from the hero Advance Estimator via query params.
  const seededAmounts = (() => {
    const net = searchParams.get("net");
    const advance = searchParams.get("advance");
    const src = searchParams.get("src");
    return {
      ...(net ? { net_commission_amount: net } : {}),
      ...(advance ? { advance_amount_requested: advance } : {}),
      ...(src ? { source: src } : {}),
    };
  })();
  // If already logged in, prefill account fields so validation passes when the user hits step 3.
  // The password value is a runtime-generated throwaway (never sent — server ignores duplicate registration).
  const [data, setData] = useState(() => {
    if (!user) return { ...initialState, ...seededAmounts };
    const throwaway = `${crypto.randomUUID?.() || Math.random().toString(36)}Ax1!`;
    return {
      ...initialState,
      ...seededAmounts,
      account_email: user.email,
      email: user.email,
      password: throwaway,
      confirm_password: throwaway,
    };
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const patch = (obj) => setData((d) => ({ ...d, ...obj }));
  const set = (k) => (e) => patch({ [k]: e?.target?.value ?? e });

  const onUploaded = (uploaded) => {
    setData((d) => ({ ...d, uploaded_files: [...(d.uploaded_files || []), uploaded] }));
  };

  const registerIfNeeded = async () => {
    if (user) return true;
    try {
      await register(data.account_email, data.password, `${data.first_name} ${data.last_name}`.trim());
      await refresh();
      toast.success("Account created — you're logged in.");
      return true;
    } catch (err) {
      const detail = formatApiErrorDetail(err?.response?.data?.detail);
      if (err?.response?.status === 409) {
        toast.error("An account with this email already exists. Please sign in first.");
        nav("/login", { state: { from: "/apply" } });
        return false;
      }
      toast.error(detail || "Could not create your account.");
      return false;
    }
  };

  const next = async () => {
    if (!validateStep(step, data)) return;
    // Create the agent account when leaving step 3 (Account Setup)
    if (step === 3 && !user) {
      const ok = await registerIfNeeded();
      if (!ok) return;
    }
    setStep((s) => Math.min(s + 1, TOTAL));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!validateStep(step, data)) {
      if (step === TOTAL) setShowConsentError(true);
      return;
    }
    setSubmitting(true);
    try {
      // 1. Record the tamper-resistant consent audit first (append-only).
      const consentPayload = buildConsentPayload(data, {
        referring_page: document.referrer || null,
      });
      const consentRes = await api.post("/consent-records", consentPayload);
      const consentRecordId = consentRes.data.consent_record_id;

      // 2. Submit the application with a reference to the consent record.
      const payload = buildPayload(data, {
        consent_record_id: consentRecordId,
        consented_at_utc: consentPayload.consented_at_utc,
      });
      await api.post("/applications", payload);
      setDone({ consent_record_id: consentRecordId });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return <SuccessScreen versions={done} onDashboard={() => nav("/dashboard")} onHome={() => nav("/")} />;
  }

  const StepComponent = STEP_COMPONENTS[step];

  return (
    <div className="max-w-4xl mx-auto px-6 py-16" data-testid="apply-page">
      <SEO
        title="Apply for a Commission Advance"
        description="Apply for up to 75% of your pending real estate commission. Simple 9-step application. No credit check."
        path="/apply"
      />
      <div className="text-center mb-10">
        <span className="overline flex items-center justify-center gap-2"><Sparkles size={14} strokeWidth={1.5}/> Application</span>
        <h1 className="font-serif-lux text-4xl sm:text-5xl mt-3">Apply for a Real Estate Commission Advance</h1>
        <p className="mt-4 text-[#5A3B31] max-w-2xl mx-auto">
          Get an advance up to 75% of your pending commission. No credit check. Fast review. Simple process.
        </p>
      </div>

      <div className="lux-card p-4 mb-6" data-testid="apply-progress-wrap">
        <div className="flex items-center justify-between mb-2 text-xs text-[#8C7A70] px-2">
          <span>Step {step} of {TOTAL}</span>
          <span className="font-semibold text-[#2A1C14]">{STEP_TITLES[step - 1]}</span>
        </div>
        <Progress value={(step / TOTAL) * 100} className="h-2 bg-[#F4EEE8]" data-testid="apply-progress" />
      </div>

      <div className="lux-card p-8 sm:p-12" data-testid="apply-form">
        <StepComponent data={data} set={set} patch={patch} onUploaded={onUploaded} showConsentError={showConsentError} />
      </div>

      <div className="flex items-center justify-between mt-8">
        <button onClick={back} disabled={step === 1} className="btn-ghost disabled:opacity-40" data-testid="apply-back">
          <ArrowLeft size={16} strokeWidth={1.5} /> Back
        </button>
        {step < TOTAL ? (
          <button onClick={next} className="btn-primary" data-testid="apply-next">
            Continue <ArrowRight size={16} strokeWidth={1.5} />
          </button>
        ) : (
          <button onClick={submit} disabled={submitting} className="btn-primary" data-testid="apply-submit">
            {submitting ? "Submitting..." : "Submit Application"}
          </button>
        )}
      </div>
    </div>
  );
}
